import { useEffect, useMemo, useState } from "react";
import * as apicall from "../components/Apicall";
import { getNavGroups } from "../components/Sidebar/sidebarNavConfig";
import { getBookingLabel } from "../utils/industryLabels";
import useExpenseBranches from "./useExpenseBranches";
import useHrAccess from "./useHrAccess";
import useModuleAccess from "./useModuleAccess";
import useWorkflowAccess from "./useWorkflowAccess";

const readLocalJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw && raw !== "undefined" ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Everything the navigation needs to decide what a person may see, resolved
 * once and shared by the desktop/tablet Sidebar and the phone bottom bar + menu
 * sheet. The context-building used to live inline in Sidebar.jsx while the
 * phone nav kept its own hand-copied list, which is how crew and support ended
 * up seeing Admin Setup (access denied) on a phone and never seeing My Tasks.
 *
 * `vendorData` is the live ezpMyProfile row Navbar fetches and hands up to
 * Layout; it wins over cached values once it arrives.
 *
 * Returns { navGroups, persona, ... } where navGroups is exactly what
 * getNavGroups() (the tested single source of truth) produces.
 */
export default function useNavContext(vendorData) {
  const userRole = apicall.getRole();
  const isTeamUser = userRole === "6";
  const isCrewUser = userRole === "5";
  const isHrUser = userRole === "7";

  // Industry: live profile first, then the cached profile. Numeric compare - the
  // value arrives as a number from the API but can be a string from storage.
  const cachedProfile = readLocalJson("vendorProfile") || readLocalJson("userDetails");
  const [profile, setProfile] = useState(null);
  const industry = Number(
    vendorData?.industry ?? profile?.industry ?? cachedProfile?.industry ?? NaN
  );
  const isClinicService = industry === 1;
  const isSportsService = industry === 7;
  const isCarService = industry === 8;
  const isSalonService = industry === 9;

  const [reportsAvailable, setReportsAvailable] = useState(false);
  const [canViewLeads, setCanViewLeads] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await apicall.get("get_reports");
        if (alive && res.status === 200) setReportsAvailable((res.data.data || []).length > 0);
      } catch (error) {
        console.error("Error fetching reports:", error);
      }
    })();
    (async () => {
      try {
        const res = await apicall.get("ezpMyProfile");
        if (alive && res.success && res.data?.[0]) setProfile(res.data[0]);
      } catch (error) {
        console.log(error);
      }
    })();
    // Lead Management is gated on canViewLeads (screen visibility), not
    // canManageLeads, which means something narrower (reassign any lead).
    (async () => {
      try {
        const res = await apicall.get("leads/my-access");
        if (alive) setCanViewLeads(!!res?.data?.canViewLeads);
      } catch {
        if (alive) setCanViewLeads(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Branch Jobs: one menu per active branch, car-service Admin/Vendor only.
  const { branches } = useExpenseBranches(isCarService && !isTeamUser && !isCrewUser && !isHrUser);
  // HR menu: only where the plan unlocks HR (Super Admin has no tenant).
  const { access: hrAccess } = useHrAccess(userRole !== "1");
  // Finance / Dealership menus: only where the Super Admin switched them on (Super Admin has no tenant).
  const { access: moduleAccess } = useModuleAccess(userRole !== "1");
  // Tasks (booking workflow steps): offered only once workflows are in use.
  const workflowEnabled = useWorkflowAccess(userRole !== "1" && !isHrUser);

  // Plan and booking terminology come from the live profile only - a cached
  // snapshot can still belong to the previous session right after login/logout.
  const labelSource = profile || vendorData;
  const planId = profile?.plan_id ?? vendorData?.plan_id;

  const navGroups = useMemo(
    () =>
      getNavGroups({
        isTeamUser,
        isCrewUser,
        isHrUser,
        isClinicService,
        isSportsService,
        isCarService,
        isSalonService,
        canViewLeads,
        planId,
        reportsAvailable,
        bookingLabel: getBookingLabel(labelSource),
        branches,
        hrAccess,
        workflowEnabled,
        moduleAccess,
      }),
    [
      isTeamUser,
      isCrewUser,
      isHrUser,
      isClinicService,
      isSportsService,
      isCarService,
      isSalonService,
      canViewLeads,
      planId,
      reportsAvailable,
      labelSource,
      branches,
      hrAccess,
      workflowEnabled,
      moduleAccess,
    ]
  );

  return {
    navGroups,
    persona: { isCrewUser, isHrUser, isTeamUser, isClinicService, isSportsService },
    profile: labelSource,
    // The new menu's Home badge (requests waiting for this person).
    approvalsPending: Number(moduleAccess?.approvals?.pending) || 0,
  };
}
